import {z} from "zod/v4-mini"
import {isInstanceOf} from "@cm/utils"
import type {Evaluator} from "@cm/graph-v3"
import {graphNode, traverseGraphParameters} from "@cm/graph-v3"
import {registerTemplateGraphNode, skipped, getTemplateNodeReturns, TypeDescriptors, visitAll, visit, TemplateNode} from "@cm/template-nodes"
import type {
    OnFilterActiveContext,
    OnCompileContext,
    TemplateNodeContext,
    TemplateNodeDefinition,
    VisitorNodeResult,
    BuilderInlet,
    GraphBuilderScope,
    NodeEvaluator,
} from "@cm/template-nodes"
import {MattressNode} from "#mattress/nodes/mattress-node"
import {MattressNodes} from "#mattress/nodes/mattress-nodes"
import type {MattressComponentGraphData, MattressFocusedComponentInstancePath} from "#mattress/nodes/component/mattress-component-types"
import {parseMattressOnContextOtherData, getMattressFilterActiveContext} from "#mattress/nodes/component/mattress-component-helpers"
import {MattressComponentInstance} from "#mattress/nodes/component/mattress-component-instance"
import {mattressComponentGraphLikeMarker} from "#mattress/role-markers"
import type {MattressComponentGraphLikeNode} from "#mattress/role-markers"

export type MattressComponentGraphParameters = {
    name: string
    nodes: MattressNodes
}

const definition: TemplateNodeDefinition<MattressComponentGraphParameters> = {
    nodeClass: "MattressComponentGraph",
    validation: {
        parameters: z.object({
            name: z.string(),
            nodes: graphNode(MattressNodes),
        }),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class MattressComponentGraph extends MattressNode<MattressComponentGraphParameters> implements MattressComponentGraphLikeNode {
    get [mattressComponentGraphLikeMarker]() {
        return true as const
    }

    public override get definition() {
        return definition
    }

    public override onFilterActive(
        context: OnFilterActiveContext,
        evaluator: Evaluator<TemplateNodeContext>,
    ): VisitorNodeResult<MattressComponentGraphParameters> {
        // Distinguish between external visit (via onFilterActive in main mattress node) and
        // internal visit (via RunCompileComponentInstance)
        // Use the presence of mattressContext to determine which one it is
        // TODO find a cleaner way of doing this
        const mattressContext = getMattressFilterActiveContext(context)

        // external visit
        if (!mattressContext) {
            const mattress = this.getParentMattressNode()
            const {focusedComponentInstancePath} = parseMattressOnContextOtherData(mattress, context)
            const focusedComponentInstanceGraph = mattressFocusedComponentInstanceGraphFromFocusedComponentInstancePath(focusedComponentInstancePath)

            // root
            if (mattress.parameters.root === this) {
                if (focusedComponentInstanceGraph) {
                    visit(focusedComponentInstanceGraph, evaluator)
                    return skipped
                } else {
                    return visitAll(this.parameters, evaluator)
                }
            } else {
                // non root
                if (focusedComponentInstanceGraph === this) {
                    return visitAll(this.parameters, evaluator)
                } else return skipped
            }
        }

        // internal visit
        if (mattressContext.root === this) return visitAll(this.parameters, evaluator)
        return skipped
    }

    public override onCompile(_context: OnCompileContext, evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<MattressComponentGraphParameters> {
        return visitAll(this.parameters, evaluator)
    }

    evaluate(scope: GraphBuilderScope, _evaluator: NodeEvaluator): BuilderInlet<MattressComponentGraphData | null> {
        return scope.pureLambda(
            scope.tuple(scope.value(this.getHash(), TypeDescriptors.String, "contentHash"), this),
            ([hash, graph]) => ({graph, contentHash: hash}),
            "mattressComponentGraphData",
        )
    }
}

/////////////////////////////////////////////////////////////////
// Mattress graph-traversal helpers.

export function mattressFocusedComponentInstanceGraphFromFocusedComponentInstancePath(
    path: MattressFocusedComponentInstancePath,
): MattressComponentGraph | null {
    if (path === "root") return null
    const focusedInstance = path.at(-1)
    if (!focusedInstance || !focusedInstance.parameters.graph) return null
    const graph = focusedInstance.parameters.graph
    return graph instanceof MattressComponentGraph ? graph : null
}

export function collectMattressNodes<T extends TemplateNode>(graph: MattressComponentGraph, predicate: (node: TemplateNode) => node is T): Set<T> {
    const collectedNodesSet: Set<T> = new Set()

    const traverse = (node: TemplateNode) => {
        traverseGraphParameters<TemplateNode>(node.parameters, isInstanceOf(TemplateNode), (x) => {
            if (predicate(x)) collectedNodesSet.add(x)
            else if (x instanceof MattressComponentInstance || x instanceof MattressComponentGraph) return
            traverse(x)
        })
    }
    traverse(graph)
    return collectedNodesSet
}
